import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
} from "react";
import { Row, Col, Button, Card, CardBody, Spinner } from "reactstrap";
import { useForm } from "react-hook-form";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import styles from "../../MainPrintListTabs/css/printlabel.module.scss";
import { useMasterStudyService } from "modules/Clinical/StudyDetails/api/useStudyQuery";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import tableStyles from "../../../../assets/scss/common/table.module.scss";
import sf from "../../../../assets/scss/common/searchFilter.module.scss";
import SimpleBar from "simplebar-react";
import { templateApiService } from "modules/Library/Template/api/template.api";
import DOMPurify from "dompurify";
import Barcode from "react-barcode";
import { createRoot } from "react-dom/client";
import { showToastMessage } from "common/service/CommonService";
import { queryClient } from "api/queryClient";
import { useGlobalStudy } from "../../../../context/StudyContext";
import { subjectLabelColumns, subjectLabelFields } from "../helper/subjectlabel.constant";
import { useSubjectLabelService } from "../api/useSubjectLabelService";
import { SubjectLabelFormValues } from "../helper/subjectlabel.type";
import CommonButton from "Components/Common/CommonButton";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import { usePermission } from "context/PermissionContext";
import { useVolunteerEnrollmentService } from "modules/Clinical/SubjectEnrollment/api/useVolunteerEnrollment";



const SubjectLabelList: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [individualCounts, setIndividualCounts] = useState<{
    [key: number]: number;
  }>({});
  const [templateHtml, setTemplateHtml] = useState<string | null>(null);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  const { UpdateLabelPrintMutation } = useSubjectLabelService();

  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SubjectLabelFormValues>({
    defaultValues: {
      studyid: null,
    },
  });

  const selectedStudyId = watch("studyid");
  const { selectedStudy } = useGlobalStudy();

  useEffect(() => {
    if (selectedStudy) {
      setValue("studyid", Number(selectedStudy.studyid));
    }
  }, [selectedStudy, setValue]);

  // Fetch all studies for the dropdown
  const { DropDownAllQuery: studySelectAllQuery } = useMasterStudyService({ enabledDropDown: true });

  const studyOptions =
    (studySelectAllQuery.data as any)?.data?.map((study: any) => ({
      label: study.studycode,
      value: study.studyid,
    })) || [];

  const selectedStudyLabel =
    studyOptions.find((opt: any) => opt.value === selectedStudyId)?.label || "";

  // Fetch volunteers based on selected study
  const { SelectAllQuery: volunteersQuery } = useVolunteerEnrollmentService({
    studyid: selectedStudyId ? Number(selectedStudyId) : null,
    searchText: searchText,
    labelPrintStatus: "U",
    filtersubjectstatus: true,

  });

  const apiResponse = volunteersQuery.data as any;
  const totalRecords = apiResponse?.data?.[0]?.totalcount || apiResponse?.totalcount || 0;

  const volunteersData = ((volunteersQuery.data as any)?.data || []).filter(
    (row: any) => {
      const isNumeric = row.subjectno && !isNaN(Number(row.subjectno)) && String(row.subjectno).trim() !== "";
      const status = row.replacementstatus;
      if (isNumeric) {
        return status !== "replaced";
      }
      return status === "replacement";
    }
  );

  // Fetch Template & Reset selection state on Study change
  useEffect(() => {
    if (selectedStudyId) {
      fetchTemplate();
    }
    setSelectedRows([]);
    setCurrentIndex(0);
    setIndividualCounts({});
    setSearchText("");
  }, [selectedStudyId]);


  const fetchTemplate = async () => {
    try {
      setIsTemplateLoading(true);
      const res = await templateApiService.getSelectAllData({
        searchtext: "Subject Label",
        status: "A",
      });
      const templates = res?.data || [];
      const labelTemplate = templates.find((t: any) => {
        const name = String(t.templatename || t.name || "").toLowerCase();
        return name.includes("subject label") || name.includes("barcode label");
      });

      if (labelTemplate) {
        setTemplateHtml(labelTemplate.designtemplate || "");
      } else if (templates.length > 0) {
        setTemplateHtml(templates[0].designtemplate || "");
      }
    } catch (error) {
      console.error("Error fetching template", error);
    } finally {
      setIsTemplateLoading(false);
    }
  };

  const selectedAssignmentIds = useMemo(
    () => selectedRows.map((row) => row.volunteerassignmentid),
    [selectedRows],
  );


  const toggleSelection = (row: any, isChecked: boolean) => {
    setSelectedRows((prev) =>
      isChecked
        ? [...prev, row]
        : prev.filter(
          (r) => r.volunteerassignmentid !== row.volunteerassignmentid,
        ),
    );
    setCurrentIndex(0); // Reset preview index when selection changes
  };

  const getCountForId = (id: number) => individualCounts[id] || 1;

  const mapSubjectToData = (row: any) => {
    const barcodeValue = row.barcode || row.subjectno;
    const barcodeHtml = `<div class="barcode-target" data-barcode="${barcodeValue}"></div>`;

    return {
      SubjectNo: row.subjectno || "N/A",
      Initials: row.initials || "N/A",
      Gender: row.gendertext || "N/A",
      StudyCode: selectedStudyLabel || "N/A",
      Barcode: "___BARCODE_MARKER___",
      barcodeno: row.barcode || "N/A",
      BarcodeHtml: barcodeHtml,
    };
  };

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      setSelectedRows(volunteersData);
    } else {
      setSelectedRows([]);
    }
    setCurrentIndex(0);
  };



  // Preview Logic
  const previewList = selectedRows.length > 0 ? selectedRows : [];
  const currentSubject = previewList[currentIndex];
  const currentQty = currentSubject
    ? getCountForId(currentSubject.volunteerassignmentid)
    : 0;

  const replacePlaceholders = (template: string, data: Record<string, any>) => {
    return template.replace(/{{(.*?)}}/g, (_, key) => {
      return data[key.trim()] ?? "";
    });
  };

  const mappedPreviewHtml = useMemo(() => {
    if (!templateHtml || !currentSubject) return null;

    const mappedData = mapSubjectToData(currentSubject);

    let renderedHtml = replacePlaceholders(templateHtml, mappedData);
    renderedHtml = DOMPurify.sanitize(renderedHtml, {
      ADD_TAGS: ["style"],
      WHOLE_DOCUMENT: true,
      FORCE_BODY: true,
    });

    return renderedHtml.replace("___BARCODE_MARKER___", mappedData.BarcodeHtml);
  }, [templateHtml, currentSubject, selectedStudyLabel]);

  // Barcode Rendering Effect - useLayoutEffect for zero-blink rendering
  useLayoutEffect(() => {
    if (previewRef.current && currentSubject) {
      const targets = previewRef.current?.querySelectorAll(".barcode-target");
      targets?.forEach((target) => {
        const val = target.getAttribute("data-barcode") || "";

        // Only render if target is empty to prevent blinking
        if (target.children.length === 0) {
          const root = createRoot(target as HTMLElement);
          root.render(
            <Barcode
              value={val}
              format="CODE128"
              width={1.5}
              height={55}
              displayValue={false}
              margin={0}
            />,
          );
        }
      });
    }
  }, [
    mappedPreviewHtml,
    selectedRows,
    individualCounts,
    volunteersQuery.isFetching,
    UpdateLabelPrintMutation.isPending,
  ]);

  const handlePrint = () => {
    if (!templateHtml || selectedRows.length === 0) return;

    const payload = selectedRows.map((row) => ({
      id: row.volunteerassignmentid,
      quantity: getCountForId(row.volunteerassignmentid),
    }));

    UpdateLabelPrintMutation.mutate(
      {
        volunteerassignment: payload,
        printtype: "Subject Label",
        studyid: selectedStudyId ? Number(selectedStudyId) : null,
      },
      {
        onSuccess: (res: any) => {
          showToastMessage(
            "success",
            res.message || "Print status updated successfully",
          );
          queryClient.invalidateQueries({
            queryKey: ["volunteer-enrollment-selectAll"],
          });
          setSelectedRows([]);
          setIndividualCounts({});
        },
        onError: (err: any) => {
          showToastMessage(
            "error",
            err.message || "Failed to update print status",
          );
        },
      },
    );
  };
  const { hasPermission } = usePermission();

  const isAllSelected =
    volunteersData.length > 0 &&
    volunteersData.every((v: any) =>
      selectedAssignmentIds.includes(v.volunteerassignmentid),
    );

  return (
    <Row>
      {/* Left Column - Form & Table */}
      <Col lg={7}>
        <div className="mb-4">
          <CommonForm
            fields={subjectLabelFields(studyOptions)}
            control={control}
            errors={errors}
            columns={2}
          />
        </div>

        {selectedStudyId && (
          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6
                className="fw-700 mb-0 small text-uppercase ls-1"
                style={{ color: "#008080" }}
              >
                Enrolled Volunteers
              </h6>
              <div
                className={`${sf.searchBox} ${searchFocused ? sf.focused : ""}`}
                style={{ width: "250px", marginBottom: 0 }}
              >
                <i className={`ri-search-line ${sf.searchIcon}`} />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchText}
                  className={sf.searchInput}
                  onChange={(e) => setSearchText(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                />
                {searchText && (
                  <i
                    className={`ri-close-line ${sf.clearIcon}`}
                    onClick={() => setSearchText("")}
                  />
                )}
              </div>
            </div>
            <CommonTableIndexWise
              columns={subjectLabelColumns(
                selectedAssignmentIds,
                toggleSelection,
                isAllSelected,
                handleSelectAll
              )}
              data={volunteersData}
              className={tableStyles.table}
              hideAction={true}
              maxHeight="400px"
              stickyHeader={true}
              loading={
                volunteersQuery.isLoading || volunteersQuery.isFetching
              }
              totalRecords={totalRecords}
              paginationShow={false}
            />
          </div>
        )}
      </Col>

      {/* Right Column - Preview */}
      {selectedStudyId && (
        <Col lg={5}>
          <Card className="border shadow-none rounded-4 overflow-hidden">
            <CardBody className="p-3 d-flex flex-column">
              <div className="mb-3">
                <h6 className="fw-700 mb-1">Label Preview</h6>
                {/* <p className="text-muted extra-small mb-0">Dymo LabelWriter 450 - 1" x 2.125"</p> */}
              </div>

              <div className={styles.preview_container} ref={previewRef}>
                {isTemplateLoading ? (
                  <div className="text-center py-4">
                    <Spinner size="sm" color="primary" />
                  </div>
                ) : currentSubject ? (
                  <div
                    className={styles.simulated_label_wrapper}
                    dangerouslySetInnerHTML={{
                      __html: mappedPreviewHtml || "",
                    }}
                  />
                ) : (
                  <div className="text-center text-muted p-4 border rounded-3 bg-white w-100">
                    <i className="ri-checkbox-line fs-1 d-block mb-2 opacity-25" />
                    <p className="small mb-0">
                      Select a subject from the list to preview their label
                    </p>
                  </div>
                )}
              </div>

              {previewList.length > 1 && (
                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <Button
                      color="light"
                      size="sm"
                      className="rounded-3 p-0 d-flex align-items-center justify-content-center border"
                      style={{
                        width: "32px",
                        height: "32px",
                        backgroundColor: "#fff",
                      }}
                      onClick={() =>
                        setCurrentIndex(Math.max(0, currentIndex - 1))
                      }
                      disabled={currentIndex === 0}
                    >
                      <i className="ri-arrow-left-s-line fs-5" />
                    </Button>

                    <div className="px-3 py-1 bg-light rounded-3">
                      <span className="fw-700 small text-primary">
                        {currentIndex + 1} of {previewList.length}
                      </span>
                    </div>

                    <Button
                      color="light"
                      size="sm"
                      className="rounded-3 p-0 d-flex align-items-center justify-content-center border"
                      style={{
                        width: "32px",
                        height: "32px",
                        backgroundColor: "#fff",
                      }}
                      onClick={() =>
                        setCurrentIndex(
                          Math.min(previewList.length - 1, currentIndex + 1),
                        )
                      }
                      disabled={currentIndex === previewList.length - 1}
                    >
                      <i className="ri-arrow-right-s-line fs-5" />
                    </Button>
                  </div>
                </div>
              )}
              {/* hide button  */}
              {hasPermission(PERMISSION_MODULES.SUBJECT_LABEL, PERMISSION_ACTIONS.PRINT) && (
                <div className="d-flex justify-content-between align-items-center mb-3 px-1">
                  <span className="text-muted small fw-semibold">Quantity</span>
                  <div className="d-flex align-items-center gap-3">
                    <Button
                      size="sm"
                      color="light"
                      className="rounded-circle p-0 d-flex align-items-center justify-content-center border shadow-sm"
                      style={{
                        width: "28px",
                        height: "28px",
                        backgroundColor: "#fff",
                      }}
                      onClick={() => {
                        if (!currentSubject) return;
                        const id = currentSubject.volunteerassignmentid;
                        setIndividualCounts((prev) => ({
                          ...prev,
                          [id]: Math.max(1, getCountForId(id) - 1),
                        }));
                      }}
                      disabled={!currentSubject || currentQty <= 1}
                    >
                      <i className="ri-subtract-line fs-6" />
                    </Button>
                    <span
                      className="fw-bold text-primary"
                      style={{ minWidth: "20px", textAlign: "center" }}
                    >
                      {currentQty}
                    </span>
                    <Button
                      size="sm"
                      color="light"
                      className="rounded-circle p-0 d-flex align-items-center justify-content-center border shadow-sm"
                      style={{
                        width: "28px",
                        height: "28px",
                        backgroundColor: "#fff",
                      }}
                      onClick={() => {
                        if (!currentSubject) return;
                        const id = currentSubject.volunteerassignmentid;
                        setIndividualCounts((prev) => ({
                          ...prev,
                          [id]: getCountForId(id) + 1,
                        }));
                      }}
                      disabled={!currentSubject}
                    >
                      <i className="ri-add-line fs-6" />
                    </Button>
                  </div>
                </div>
              )}

              <CommonButton
                module={PERMISSION_MODULES.SUBJECT_LABEL}
                action={PERMISSION_ACTIONS.PRINT}
                color="primary"
                block
                className="py-2 rounded-3 d-flex align-items-center justify-content-center gap-2 fw-700 shadow-sm mt-auto"
                disabled={previewList.length === 0 || UpdateLabelPrintMutation.isPending}
                onClick={handlePrint}
              >
                {UpdateLabelPrintMutation.isPending ? (
                  <Spinner size="sm" />
                ) : (
                  <i className="ri-printer-line" />
                )}{" "}
                Print Labels
              </CommonButton>
            </CardBody>
          </Card>
        </Col>
      )}
    </Row>
  );
};

export default SubjectLabelList;
